import { useEffect } from 'react';

import { BaseRow } from '@sbu/components.datagrid';

/**
 * Hook to sync selectedRow and selectedRows with the latest data when data changes.
 * This ensures updates to the apollo cache are reflected in the side panel when the side panel is open
 *
 * @param currentData - The current array of data from the query
 * @param selectedRow - The currently selected single row
 * @param selectedRows - The currently selected multiple rows
 * @param onClickRow - Function to update the selected row
 * @param onSelectMultiple - Function to update the selected rows
 * @param getRowId - Function to extract unique identifier from row (defaults to 'id' field converted to string)
 */
export const useDataGridRowSync = <Row extends BaseRow>({
  currentData,
  selectedRow,
  selectedRows,
  onClickRow,
  onSelectMultiple,
  getRowId = (row) => String(row.id),
}: {
  currentData?: Row[];
  selectedRow?: Row;
  selectedRows?: Row[];
  onClickRow: (row: Row) => void;
  onSelectMultiple: (rows?: Row[]) => void;
  getRowId?: (row: Row) => string;
}) => {
  useEffect(() => {
    if (!currentData) {
      return;
    }

    // Sync single selected row
    if (selectedRow) {
      const selectedRowId = getRowId(selectedRow);
      const updatedSelectedRow = currentData.find(
        (row) => getRowId(row) === selectedRowId
      );

      if (updatedSelectedRow && updatedSelectedRow !== selectedRow) {
        onClickRow(updatedSelectedRow);
      }
    }

    // Sync multiple selected rows
    if (selectedRows && selectedRows.length > 0) {
      const updatedSelectedRows = selectedRows
        .map((selectedRow) => {
          const selectedRowId = getRowId(selectedRow);
          return currentData.find((row) => getRowId(row) === selectedRowId);
        })
        .filter((row): row is Row => row !== undefined);

      if (updatedSelectedRows.length > 0) {
        const hasChanges = updatedSelectedRows.some(
          (updatedRow, index) => updatedRow !== selectedRows[index]
        );

        if (hasChanges) {
          onSelectMultiple(updatedSelectedRows);
        }
      }
    }
  }, [
    currentData,
    selectedRow,
    selectedRows,
    onClickRow,
    onSelectMultiple,
    getRowId,
  ]);
};
